/**
 * @author Matěj Grabovský <matej.grabovsky@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import AssertionError from 'assertion-error';
import groupBy from 'lodash/groupBy';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import NetworkType from '../network/network-type';

/**
 * @import { FingerprintManager, FingerprintStrategy } from '.'
 * @import { FirmwareManager } from '../firmware'
 * @import FingerprintHasher from './fingerprint-hasher'
 * @import { NetworkManager } from '../network'
 * @import { Logger } from '../logger/logger'
 * @import NetworkConnection from '../network/network-connection'
 * @import { UpdateUrlConfig } from '../update-url-config'
 */

/**
 * Generátor informací pro otisk z informací dostupných z hardwaru zařízení.
 * @implements {FingerprintManager}
 */
export default class FingerprintHardware {
  /**
   * Vytvoří instanci.
   * @param {FingerprintStrategy} fingerprintStrategy
   * @param {FirmwareManager} firmwareManager Správce firmware.
   * @param {FingerprintHasher} hasher Objekt pro hashování informací.
   * @param {Logger} logger
   * @param {NetworkManager} networkManager Správce síťových připojení.
   */
  constructor(
    fingerprintStrategy,
    firmwareManager,
    hasher,
    logger,
    networkManager
  ) {
    this.fingerprintStrategy = fingerprintStrategy;
    this.firmwareManager = firmwareManager;
    this.hasher = hasher;
    this.logger = logger;
    this.networkManager = networkManager;
  }

  /**
   * Najde vhodnou MAC adresu pro generování otisku. V první řadě se vybere
   * lexikograficky nejmenší MAC adresa ethernetového adaptéru. Pokud taková
   * neexistuje, vybere se lexikograficky nejmenší adresa Wi-Fi adaptéru. Pokud
   * ani tato neexistuje, vrátí undefined.
   * @private
   * @returns {Promise<string | undefined>} MAC adresa nebo undefined, pokud nelze žádnou vhodnou najít.
   */
  async findMac() {
    const lanMac = await this.findLeastMacByType(NetworkType.Ethernet);
    if (lanMac) {
      return lanMac;
    }
    const wlanMac = await this.findLeastMacByType(NetworkType.Wifi);
    if (wlanMac) {
      return wlanMac;
    }
  }

  /**
   * @param {NetworkType} networkType
   * @returns {Promise<string | undefined>}
   */
  async findLeastMacByType(networkType) {
    const connections = await this.getConnections(networkType);
    return connections
      .map((connection) => connection.getMac())
      .map((mac) => this.normalizeMac(mac))
      .sort()[0];
  }

  /** @type {FingerprintManager['generate']} */
  async generate() {
    const fingerprintInfo = await this.getInfo(this.fingerprintStrategy);
    return this.hasher.hashObject(fingerprintInfo);
  }

  /**
   * @param {NetworkType} type
   * @returns {Promise<NetworkConnection[]>}
   */
  async getConnections(type) {
    const connections = groupBy(
      await this.networkManager.getAllConnections(),
      (connection) => connection.getType()
    );
    return connections[type] ?? [];
  }

  /**
   * Na základě strategie vrátí požadovanou hodnotu.
   * @param {FingerprintStrategy} strategy
   * @returns {Promise<Object>}
   */
  async getInfo(strategy) {
    /** @type {Promise<string | undefined>} */
    let promiseValue = Promise.resolve(undefined);
    /** @type {string | undefined} */
    let key;
    switch (strategy) {
      case 'snmac':
        return this.getSnMacInfo();
      case 'sn':
        key = 'serialNumber';
        promiseValue = this.firmwareManager.getSerialNumber();
        break;
      case 'mac':
        key = 'mac';
        promiseValue = this.findMac();
        break;
      case 'lanmac':
        key = 'mac';
        promiseValue = this.findLeastMacByType(NetworkType.Ethernet);
        break;
      case 'wlanmac':
        key = 'mac';
        promiseValue = this.findLeastMacByType(NetworkType.Wifi);
        break;
      default:
        break;
    }

    try {
      const value = await promiseValue.catch(() => undefined);
      if (!value) {
        throw new Error(`${strategy} is not suitable for this device`);
      }
      if (!key) {
        // Toto může nastat pouze programátorskou chybou.
        throw new AssertionError(`${strategy} - key not defined`);
      }
      return { [key]: value };
    } catch (err) {
      this.logger.error(err);
      // Fallback, pokud nelze použít vybranou strategii.
      return this.getSnMacInfo();
    }
  }

  /**
   * Zjistí informace z hardwaru, ze kterých lze generovat otisk.
   * @private
   * @returns {Promise<Object>} Komponenty otisku zařízení.
   * Zatím se používají položky `mac` a `serialNumber`. Pokud je dostupné multiInstanceId, tak se použije i to.
   */
  async getSnMacInfo() {
    const mac = await this.findMac();
    if (isNil(mac)) {
      // Pokud nebyla nalezena vhodná MAC adresa, nemáme s čím pracovat a
      // vyhodíme chybu.
      throw new Error('Cannot find a suitable MAC address.');
    }
    const serialNumber = await this.firmwareManager.getSerialNumber();
    const info = { mac, serialNumber };
    const multiInstanceId = await this.firmwareManager.getMultiInstanceId();
    if (!isNil(multiInstanceId)) {
      return { ...info, multiInstanceId };
    }
    return info;
  }

  /**
   * Normalizuje MAC adresu na společný formát, tj. dvanáct minuskulních
   * hexadecimálních číslic oddělených po dvou dvoutečkou.
   * @param {string} mac Vstupní MAC adresa.
   * @private
   * @returns {string} Normalizovaná MAC adresa.
   */
  normalizeMac(mac) {
    // Regulární výraz pro normalizovanou MAC adresu.
    const macRegex = /^[0-9a-f]{2}(:[0-9a-f]{2}){5}$/i;
    if (!isString(mac) || !macRegex.test(mac)) {
      throw new Error(`Invalid MAC address "${mac}".`);
    }
    const normalized = mac.toLowerCase();
    return normalized;
  }
}

/**
 * @param {string} maybeFingerprintStrategy
 * @returns {FingerprintStrategy | undefined}
 */
export function parseFingerprintStrategy(maybeFingerprintStrategy) {
  const trimmed = maybeFingerprintStrategy.trim();
  switch (trimmed) {
    case 'snmac':
    case 'sn':
    case 'mac':
    case 'lanmac':
    case 'wlanmac':
      return trimmed;
    default:
      break;
  }
}
